import React, { useRef, useMemo } from 'react';

import * as QS from './question-shared';

export interface TextWithSelectionQuestionProps extends QS.QuestionProps {
    showValidationMessage: boolean;
    validationLabel?: string;
}

export const TextWithSelectQuestion: React.FC<TextWithSelectionQuestionProps> = ({
    selector,
    styleName,
    children,
    type,
    value,
    placeholder,
    values,
    showValidationMessage,
    onChange,
    validationLabel,
    disabled,
}) => {
    const inputRef = useRef<HTMLInputElement>();
    const selectRef = useRef<HTMLSelectElement>();

    const computedStyle = {
        ...QS.styles.default,
        ...(styleName ? QS.styles[styleName] : {}),
    };

    const handleChange = useMemo(() => onChange as QS.onChangeType, [onChange]);

    return (
        <fieldset
            id={`text-with-select-${selector}`}
            data-testid={`text-with-select-${selector}`}
            style={computedStyle.fieldset}
            className={styleName && QS.classNames[styleName].fieldset}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <legend
                    id={`text-with-select-label-${selector}`}
                    style={{
                        ...computedStyle.legend,
                        alignContent: 'flex-start',
                        flex: 3,
                    }}
                    className={styleName && QS.classNames[styleName].legend}
                >
                    {children}
                </legend>

                <div
                    style={{
                        alignContent: 'flex-end',
                        flex: 1,
                        marginRight: '10px',
                    }}
                >
                    <input
                        type={QS.inputType(type)}
                        ref={inputRef}
                        id={`text-with-select-textbox-${selector}`}
                        data-testid={`text-with-select-textbox-${selector}`}
                        value={(value as QS.TextWithSelectValue)?.text || ''}
                        className={styleName && QS.classNames[styleName].input}
                        style={{ ...computedStyle.input, maxWidth: '100%' }}
                        onChange={(e) =>
                            handleChange &&
                            handleChange({
                                text: e.target.value,
                                select: selectRef.current.value,
                            })
                        }
                        placeholder={placeholder}
                        aria-labelledby={`text-with-select-label-${selector}`}
                        disabled={disabled}
                    />
                </div>
                <div style={{ alignContent: 'flex-end', flex: 1 }}>
                    <select
                        ref={selectRef}
                        id={`text-with-select-dropdown-${selector}`}
                        data-testid={`text-with-select-dropdown-${selector}`}
                        value={(value as QS.TextWithSelectValue)?.select || ''}
                        onChange={(e) =>
                            handleChange &&
                            handleChange({
                                text: inputRef.current.value,
                                select: e.target.value,
                            })
                        }
                        aria-labelledby={`text-with-select-label-${selector}`}
                        disabled={disabled}
                    >
                        {values?.map((v, i) => (
                            <option
                                key={i}
                                value={v.value}
                            >
                                {v.display}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {showValidationMessage && QS.validationMessage(type, validationLabel)}
        </fieldset>
    );
};
